import {Component, computed, effect, inject, input, OnDestroy, OnInit, signal, untracked} from '@angular/core';
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {DatePipe, DecimalPipe, Location} from "@angular/common";
import {ToCapitalizedTextPipe} from "src/app/presentation/common/pipes/ToCapitalizedTextPipe";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {timeAgo} from 'src/app/domain/util/timestamps/timestamps';
import {AssetSampleApiClient} from "src/app/infrastructure/moovement-api/web-api/asset-sample.api-client";
import {
  allAnimalTypes,
  Animal,
  AnimalType, AnimalMetadata,
  DeviceAssociation,
  Paddock,
  Pregnancy,
  SynchronizationStatus,
  WeightMeasurement, WeightMeasurementUnit
} from "src/app/infrastructure/moovement-api/models/asset";
import {ascendingAssetSamples, compare, last, sinceOneDay} from "src/app/app.config";
import {sevenDays} from "src/app/presentation/common/globals";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {Subject} from "rxjs";
import {
  AnimalActivityComponent
} from "src/app/presentation/main-view/information-center/animals/animal-details/animal-activity/animal-activity.component";
import {AssetMetadataComponent} from "src/app/presentation/common/asset-metadata/asset-metadata.component";
import {
  AnimalAlertsBarComponent
} from "src/app/presentation/main-view/information-center/animals/animal-details/animal-alerts-bar/animal-alerts-bar.component";
import {
  AnimalsInRangeComponent
} from 'src/app/presentation/main-view/information-center/animals/animal-details/animals-in-range/animals-in-range.component';
import {AggregatedAssetState} from 'src/app/domain/assets/aggregated-state/aggregated-asset-state';
import {MapStore} from 'src/app/domain/map/map.store';
import {
  AddWeightMeasurementRequest,
  AnimalApiClient
} from 'src/app/infrastructure/moovement-api/web-api/animal.api-client';
import {WeightMeasurementUnitPipe} from "src/app/presentation/common/pipes/WeightMeasurementUnitPipe";
import {
  CreateWeightMeasurementComponent
} from "src/app/presentation/main-view/information-center/animals/add-weight-measurement/create-weight-measurement.component";
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {unassignedAnimalsPaddock} from 'src/app/domain/assets/paddocks/unassigned-animals-paddock';
import {AggregatedAssetStateStore} from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.store';
import {Router} from '@angular/router';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {batteryIcon, batteryLevelColor} from 'src/app/domain/assets/battery/battery';
import {
  animalLowVoltageThreshold,
  animalMidVoltageThreshold
} from 'src/app/presentation/common/asset-details.component';
import {
  AnimalDetailsContainerComponent
} from "src/app/presentation/main-view/information-center/animals/animal-details/animal-details-container/animal-details-container.component";
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {DeviceType} from 'src/app/domain/devices/device';
import {PaddockStore} from 'src/app/domain/assets/paddocks/paddock.store';
import {RssiService} from 'src/app/domain/samples/rssi/rssi.service';
import {Color, LineChartModule, ScaleType, Series} from "@swimlane/ngx-charts";
import {
  calculateSeries,
  maxRssi,
  toPercentage
} from 'src/app/presentation/main-view/information-center/animals/animal-details/signal-strength-calculations';
import {LocationStore} from 'src/app/domain/samples/location/location.store';
import {LocationService} from 'src/app/domain/samples/location/location.service';
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {AnimalSelectionStore} from 'src/app/domain/assets/animals/animal-selection.store';
import {UserStore} from 'src/app/domain/user/user.store';
import {AggregatedDeviceStateStore} from "src/app/domain/devices/aggregated-state/aggregated-device-state.store";
import {ToPregnancyStatusIconPipe} from "src/app/presentation/common/pipes/ToPregnancyStatusIconPipe";
import {PregnancyService} from "src/app/domain/services/pregnancy.service";

import {ScheduledUpdates} from 'src/app/infrastructure/scheduled-updates/scheduled-updates';
import {
  AnimalButtonComponent
} from "src/app/presentation/main-view/information-center/animals/animal-button/animal-button.component";
import {ActionButtonComponent} from "src/app/presentation/common/action-button/action-button.component";
import {NgbDatepicker, NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {FormsModule} from "@angular/forms";
import {NfcLocationMetadataStore, NfcLocationStore} from "src/app/domain/samples/nfc-location/nfc-location.store";
import {NfcLocationService} from "src/app/domain/samples/nfc-location/nfc-location.service";
import AssetSample = Samples.AssetSample;
import NfcGpsFix = Samples.NfcGpsFix;
import NfcLocationSampleMetadata = Samples.NfcLocationSampleMetadata;

interface PreviousInput {
  unit?: WeightMeasurementUnit
  date: NgbDateStruct
}


const now = new Date();

const today: NgbDateStruct = {
  year: now.getFullYear(),
  month: now.getMonth() + 1,
  day: now.getDate()
}

const previousInput: PreviousInput = {
  date: today
}

@Component({
  selector: 'app-animal-details',
  standalone: true,
  imports: [
    DecimalPipe,
    GenericButtonComponent,
    ToCapitalizedTextPipe,
    AnimalsInRangeComponent,
    AnimalActivityComponent,
    AssetMetadataComponent,
    AnimalAlertsBarComponent,
    WeightMeasurementUnitPipe,
    DatePipe,
    CreateWeightMeasurementComponent,
    AnimalDetailsContainerComponent,
    LineChartModule,
    ToPregnancyStatusIconPipe,
    AnimalButtonComponent,
    ActionButtonComponent,
    NgbDatepicker,
    FormsModule
  ],
  templateUrl: './animal-details.component.html',
  styleUrl: './animal-details.component.css'
})
export class AnimalDetailsComponent implements OnInit, OnDestroy {

  id = input.required<string>();

  currentAnimal = signal<Animal | undefined>(undefined);

  parentsOfCurrentAnimal = signal<Animal[]>([]);

  childrenOfCurrentAnimal = signal<Animal[]>([]);

  deviceId = signal<string | undefined>(undefined);

  canBeAltered = false;
  canBeDeleted = false;

  loading = false;

  relatedDeviceAssociations = signal<DeviceAssociation[]>([]);

  statusMessage = signal('Last seen: ?');

  destroy$ = new Subject<void>();

  batteryMidThreshold = animalMidVoltageThreshold
  batteryLowThreshold = animalLowVoltageThreshold

  batteryVoltage = signal<number | undefined>(undefined)
  batterLevelColor = signal('color-primary-information');
  batteryIcon = signal('fa-solid fa-battery-empty');
  lastRssi = signal<number | undefined>(undefined);

  loadingRssis = signal(false);
  loadedRssis = signal(false);
  rssiSeries = signal<Series[]>([]);
  rssiScheme: Color = {
    name: 'default',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#217dd9']
  };

  originalName?: string;
  originalComment?: string;
  originalMetadata?: AnimalMetadata;
  outOfBoundsSince = signal<Date | undefined>(undefined)
  stoppedMovingSince = signal<Date | undefined>(undefined)
  abnormalActivitySince = signal<Date | undefined>(undefined)
  abnormalActivityIsHigh = signal(false)
  pastLocationsButtonText: string = "Show historic locations"
  assignedPaddock?: Paddock
  paddocks = signal<Paddock[]>([])
  years: Array<number> = []
  animalTypes: Array<AnimalType> = allAnimalTypes

  protected dateOfBirth = previousInput.date;

  protected today = today;

  nfcLocationStore = inject(NfcLocationStore);
  nfcLocationMetadataStore = inject(NfcLocationMetadataStore);
  nfcLocationService = inject(NfcLocationService);
  //Generic data:
  lastSeenByGateway = signal<Date | undefined>(undefined)
  lastSeenMessage = signal<string | undefined>('?')

  //Location data:
  lastLocationFix = signal<Date | undefined>(undefined);
  lastLocationFixMessage = signal('?');
  lastSeenByLoRaGatewayMessage = signal('?');

  lastSeenByBLEScanner = signal<Date | undefined>(undefined);
  lastSeenByBLEScannerMessage = signal('?');

  // lastSeenBySatellite = signal<Date | undefined>(undefined)
  // lastLocationFixMessage = signal<string | undefined>('?')
  // lastSeenByBLEScannerMessage = signal<string | undefined>('?')

  coordinates = signal<number[] | undefined>(undefined);
  nfcGpsFixes = signal<Samples.Sample<Samples.NfcGpsFix>[]>([])
  // TODO demo only: caches the fabricated arrival instant per gpsFix so it isn't re-randomized on every change detection run
  private demoArrivalInstants = new Map<Samples.Sample<Samples.NfcGpsFix>, string>();
  nfcGpsFixesMap = signal<{from: AssetSample<Samples.NfcGpsFix>, to: AssetSample<Samples.NfcGpsFix>}[]>([])
  showDistance = signal(true);
  private pregnancyService = inject(PregnancyService);

  isPregnant = computed(() => this.pregnancyService.get(this.currentAnimal()?.id)?.() ?? false)
  // accelerometer data:
  numberOfInterruptsLast24Hours?: number = 0;

  // battery data
  lastSeenBatteryLevelMessage: string = 'loading detailed battery data'

  loadingWeightMeasurements = signal(true);
  weightMeasurements = signal<WeightMeasurement[]>([]);

  weightMeasurement?: AddWeightMeasurementRequest;

  nfcScanMoments = signal<NfcLocationSampleMetadata[]>([]);
  showBatteryData = signal(false);
  showGpsData = signal(false);
  showBLEData = signal(false);
  showGatewayData = signal(false);
  showAccelerometerData = signal(false);

  locationStore = inject(LocationStore);

  private readonly aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  private readonly aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  private readonly animalSelectionStore = inject(AnimalSelectionStore);
  private readonly animalStore = inject(AnimalsStore);
  private readonly deviceStore = inject(DeviceStore);
  private readonly deviceAssociationStore = inject(DeviceAssociationStore);
  private readonly farmStore = inject(FarmStore);
  private readonly internalNotificationsStore = inject(InternalNotificationsStore);
  private readonly mapStore = inject(MapStore);
  private readonly paddockStore = inject(PaddockStore);
  private readonly userStore = inject(UserStore);
  private readonly router = inject(Router);
  private readonly assetSampleApiClient = inject(AssetSampleApiClient);
  protected readonly pregnancyHistory = signal<Pregnancy[]>([]);
  protected readonly sortedPregnancyHistory = computed(() =>
    [...this.pregnancyHistory()].sort((a, b) => {
      const aTime = a.dueDate ? Date.parse(a.dueDate) : 0;
      const bTime = b.dueDate ? Date.parse(b.dueDate) : 0;
      return bTime - aTime;
    })
  );
  protected readonly loadingPregnancyHistory = signal<boolean>(false);
  protected readonly canGetPregnant = signal<boolean | null>(null);

  constructor(
    protected dataSynchronisationService: DataSynchronisationService,
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService,
    private readonly animalApiClient: AnimalApiClient,
    private readonly locationService: LocationService,
    private readonly rssiService: RssiService
  ) {
    effect(() => {
      const id = this.id();
      if (!id) return;
      const animal = this.animalStore.entityMap()[id];
      this.currentAnimal.set(animal);
      this.parentsOfCurrentAnimal.set(this.aggregatedAssetStateStore.entityMap()[id]?.parents || [])
      this.childrenOfCurrentAnimal.set(this.aggregatedAssetStateStore.entityMap()[id]?.children || [])
      this.canGetPregnant.set(
        animal?.type == 'COW' || animal?.type == 'UNKNOWN' || animal?.type == 'HEIFER'
      )
    });

    effect(() => {
      this.animalApiClient.getPregnancyStatus(this.id()).subscribe({
        next: value => {
          this.pregnancyHistory.set(value)
          this.calculatePregnancy(value);
        }
      })
    });

    effect(() => {
      const id = this.id();
      if (!id) return;

      const state = this.aggregatedAssetStateStore.entityMap()[id];
      if (!state) return;
      // this.nfcGpsFixes.set(this.nfcLocationStore.locationSamples().filter(sample => sample.assetId === id).map(sample => sample.sample));
      // const from: AssetSample<Samples.NfcGpsFix>[] = []
      // const to: AssetSample<Samples.NfcGpsFix>[] = []
      // for (let i = 0; i < this.nfcGpsFixes().length; i++) {
      //   if (i % 2 == 0) {
      //     to[i] = this.nfcGpsFixes().at(i)!
      //   } else {
      //     from[i] = this.nfcGpsFixes().at(i)!
      //   }
      // }
      // this.nfcGpsFixesMap.set()
      this.coordinates.set(state.position);
    });

    effect(() => {
      const id = this.id();
      if (!id) return;

      const deviceId = this.animalStore.animalIdToDeviceId().get(id);
      this.deviceId.set(deviceId);
    });

    effect(() => {
      const deviceId = this.deviceId();
      if (!deviceId) return;

      this.rssiService.latestBatteryVoltages([deviceId]).subscribe(voltages => {
        if (!voltages || voltages.length === 0) return;

        const latestVoltage = voltages[0];
        const maxRssiRaw = maxRssi(latestVoltage);
        let maxRssiPercentage = toPercentage(maxRssiRaw);
        this.lastRssi.set(maxRssiPercentage);
      });
    });

    effect(() => {
      this.canBeAltered = this.userStore.canEditPersistable(this.currentAnimal());
      this.canBeDeleted = this.userStore.canDeletePersistable(this.currentAnimal());
    });

    effect(() => {
      const deviceAssociations = this.deviceAssociationStore.entities();
      this.relatedDeviceAssociations.set(deviceAssociations.filter(deviceAssociation => deviceAssociation.assetId === this.id()));
    });

    effect(() => {
      const deviceId = this.deviceId();
      if (!deviceId) return;

      const deviceType = this.deviceStore.deviceIdToType().get(deviceId);
      if (!deviceType) return;

      if (deviceType === DeviceType.GPS_PROXIMITY_ACCELEROMETER_TAG) {
        this.showAccelerometerData.set(true);
        this.showBatteryData.set(true);
        this.showDistance.set(true);
        this.showGatewayData.set(true);
        this.showGpsData.set(false);
        this.showBLEData.set(true);
      } else if (deviceType === DeviceType.GPS_TAG) {
        this.showAccelerometerData.set(false);
        this.showBatteryData.set(true);
        this.showDistance.set(true);
        this.showGatewayData.set(true);
        this.showGpsData.set(true);
        this.showBLEData.set(false);
      } else if (deviceType === DeviceType.PROXIMITY_TAG) {
        this.showAccelerometerData.set(false);
        this.showBatteryData.set(false);
        this.showDistance.set(false);
        this.showGatewayData.set(false);
        this.showGpsData.set(false);
        this.showBLEData.set(true);
      }
    });

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      if (!this.showBatteryData()) return;

      untracked(() => this.loadBatteryData(assetId))
    });

    effect(() => this.loadWeightMeasurements());

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      if (!this.showAccelerometerData()) return;

      this.loadAccelerationData(assetId);
    });

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      if (!this.animalHasNfcTag()) return;

      if (this.aggregatedAssetStateStore.loading()) return;

      const state = this.aggregatedAssetStateStore.entityMap()[assetId];
      untracked(() => this.nfcLocationService.loadLocations(assetId, state));

      this.nfcScanMoments.set([...this.nfcLocationMetadataStore.metadata()].sort((a, b) => b.sampleCount - a.sampleCount));
      this.nfcGpsFixes.set(this.nfcScanMoments().flatMap(scanMoment => scanMoment.samples) || [])
    });

    effect(() => {
      const assetId = this.currentAnimal()?.id;
      if (!assetId) return;

      const aggregatedAnimalState = this.aggregatedAssetStateStore.entityMap()[assetId];

      this.lastLocationFix.set(aggregatedAnimalState?.lastGPSFixAt);
      this.lastSeenByGateway.set(aggregatedAnimalState?.lastSeenByGatewayAt);

      const observingDeviceId = aggregatedAnimalState?.lastObservingDevice;
      if (!observingDeviceId) {
        this.lastSeenByBLEScanner.set(aggregatedAnimalState?.lastSeenByBLEScannerAt);
      } else {
        const deviceId = this.deviceId();
        const observingDeviceState = this.aggregatedDeviceStateStore.entityMap()[observingDeviceId];
        if (!deviceId || !observingDeviceState) return;

        this.lastSeenByBLEScanner.set(observingDeviceState.lastObservationPerDevice.get(deviceId));
      }
    });
    effect(() => {
      const aggregatedAnimalState = this.aggregatedAssetStateStore.entityMap()[this.id()];
      if (!aggregatedAnimalState) return;
      const lastSeen = last(aggregatedAnimalState.lastSeenByBLEScannerAt, aggregatedAnimalState.lastSeenByGatewayAt, aggregatedAnimalState.lastGPSFixAt)
      this.lastSeenMessage.set(timeAgo(lastSeen));

      this.lastLocationFix.set(aggregatedAnimalState?.lastGPSFixAt);
      this.lastSeenByGateway.set(aggregatedAnimalState?.lastSeenByGatewayAt);
      this.lastSeenByBLEScannerMessage.set(timeAgo(aggregatedAnimalState.lastSeenByBLEScannerAt));

      this.batteryVoltage.set(aggregatedAnimalState.batteryVoltage);
      this.batterLevelColor.set(batteryLevelColor(aggregatedAnimalState.batteryVoltage, this.batteryLowThreshold, this.batteryMidThreshold));
      this.batteryIcon.set(batteryIcon(aggregatedAnimalState.batteryVoltage, this.batteryLowThreshold, this.batteryMidThreshold));

      this.outOfBoundsSince.set(aggregatedAnimalState.outOfPaddockSince);

      this.stoppedMovingSince.set(aggregatedAnimalState.stoppedMovingSince);

      this.abnormalActivitySince.set(aggregatedAnimalState.abnormalActivitySince);
      this.abnormalActivityIsHigh.set(aggregatedAnimalState.abnormalActivityStatus === Samples.AlertStatus.ABNORMALLY_HIGH_ACTIVITY);
    });

    effect(() => {
      this.assignedPaddock = this.paddocks().find(paddock => this.currentAnimal()?.assignedPaddockId == paddock.id) || unassignedAnimalsPaddock;
    });

    effect(() => {
      const animal = this.currentAnimal();
      if (!animal) return;

      if (!this.originalName) this.originalName = animal.visualTag
      if (!this.originalComment) this.originalComment = animal.comment
      if (!this.originalMetadata) this.originalMetadata = animal.animalMetadata
    });

    effect(() => {
      if (this.paddockStore.loading()) return;

      const paddocks = this.paddockStore.entities();
      this.paddocks.set([...paddocks, unassignedAnimalsPaddock]);
    });

    effect(() => {
      if (!this.showDistance()) return;

      if (this.aggregatedAssetStateStore.loading() || this.farmStore.loading()) return;

      const id = this.id();
      const farm = this.farmStore.farm();
      const deviceId = this.deviceId();
      if (!farm || !deviceId) return;

      const deviceType = this.deviceStore.deviceIdToType().get(deviceId);
      if (!deviceType || deviceType === DeviceType.NFC_TAG) return;

      const state = this.aggregatedAssetStateStore.entityMap()[id];
      untracked(() => this.locationService.loadLocations(id, farm.skipAnomalousLocations, state));
    });
    effect(() => {
      this.setLastSeenBySatellite(this.lastLocationFix());
    });

    effect(() => {
      this.setLastSeenByGateway(this.lastSeenByGateway());
    });

    effect(() => {
      this.setLastSeenByBLEScanner(this.lastSeenByBLEScanner());
    });
  }

  private calculatePregnancy(value: Pregnancy[]) {
    const pregnancy = value.find(it => it.status == 'CONFIRMED' || it.status == 'ESTIMATED');
    this.pregnancyService.update(this.id(), pregnancy);
  }

  private setLastSeenByBLEScanner(lastSeenByBLEScanner?: Date) {
    this.lastSeenByBLEScannerMessage.set(timeAgo(lastSeenByBLEScanner, ''));
  }

  private setLastSeenBySatellite(lastSeenBySatellite?: Date) {
    this.lastLocationFixMessage.set(timeAgo(lastSeenBySatellite, ''));
  }

  private setLastSeenByGateway(lastSeenByGateway?: Date) {
    this.lastSeenByLoRaGatewayMessage.set(timeAgo(lastSeenByGateway, ''));
  }

  ngOnInit(): void {
    this.years = [];
    const today = new Date();
    for (let i = 0; i < 10; i++) {
      this.years.push(today.getFullYear() - i);
    }

    this.showAnimalLocations();
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }


  async setDateOfBirth(date: NgbDateStruct) {
    previousInput.date = date;
    this.dateOfBirth = date;

    if (this.canBeAltered) {
      this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          yearOfBirth: date.year.toString(),
          dateOfBirth: new Date(date.year, date.month+1, date.day).toISOString(),
        }
      });
      await this.dataSynchronisationService.applyAction(
        DataType.animals,
        ActionType.mergeAsset,
        this.currentAnimal()!,
        SynchronizationStatus.syncPending
      );
      this.internalNotificationsStore.addNotification("Successfully changed date of birth.", InternalNotificationType.SUCCESS, false);
    }
  }
  // createMessage() {
  //   const assetId = this.id();
  //   if (!assetId) return;
  //
  //   const state = this.aggregatedAssetStateStore.entityMap()[assetId];
  //   if (!state) return;
  //
  //   if (state.stoppedMovingSince) {
  //     this.statusMessage.set(`Stopped moving ${timeAgo(state.stoppedMovingSince)}`);
  //   } else if (state.outOfFarmSince) {
  //     this.statusMessage.set(`Out of farm since ${timeAgo(state.outOfFarmSince)}`);
  //   } else if (state.outOfPaddockSince) {
  //     this.statusMessage.set(`Out of paddock since ${timeAgo(state.outOfPaddockSince)}`);
  //   } else {
  //     this.statusMessage.set(`Last seen ${timeAgo(this.lastSeen(state))}`);
  //   }
  // }

  protected animalHasNfcTag(): boolean {
    const deviceId = this.deviceId();
    if (!deviceId) return false;
    const deviceType = this.deviceStore.deviceIdToType().get(deviceId);
    return deviceType === DeviceType.NFC_TAG;
  }

  private lastSeen(state: AggregatedAssetState) {
    if (!state.lastSeenByGatewayAt) return state.lastSeenByBLEScannerAt;
    if (!state.lastSeenByBLEScannerAt) return state.lastSeenByGatewayAt;

    if (state.lastSeenByGatewayAt > state.lastSeenByBLEScannerAt) return state.lastSeenByGatewayAt;

    return state.lastSeenByBLEScannerAt;
  }

  private showAnimalLocations() {
    this.mapStore.showAnimalLocationLayer();
    this.mapStore.hideHistoricAnimalLocationLayer();
  }

  showHistoricalLocations() {
    this.router.navigate(['main/animal-details', this.id(), 'historical-locations']);
  }

  confirmPregnancyById(id?: string) {
    this.setAnimalType(AnimalType.COW)
    this.animalApiClient.updatePregnancyStatus(this.id(), {
      id: id,
      status: 'CONFIRMED'
    }).subscribe({
      next: value => {
        this.animalApiClient.getPregnancyStatus(this.id()).subscribe({
          next: pregnancyHistory => {
            this.pregnancyHistory.set(pregnancyHistory)
            this.calculatePregnancy(pregnancyHistory);
          }
        })
        console.log("added pregnancy status!")
      }
    })
  }

  confirmPregnancy() {
    if (confirm("Do you wish to mark this animal as pregnant?")) {

      if (this.currentAnimal()?.type != AnimalType.COW) {
        this.setAnimalType(AnimalType.COW)
      }
      const dueDate = getDueDate()
      this.animalApiClient.addPregnancyStatus(this.id(), {
        chance: 100, dueDate: dueDate.toISOString(), id: randomUuid()
      }).subscribe({
        next: value => {
          this.animalApiClient.getPregnancyStatus(this.id()).subscribe({
            next: pregnancyHistory => {
              this.pregnancyHistory.set(pregnancyHistory)
              this.calculatePregnancy(pregnancyHistory);
            }
          })
          console.log("added pregnancy status!")
        }
      })
    }
  }

  undoPregnancy(id?: string) {
    this.animalApiClient.updatePregnancyStatus(this.id(), {
      id: id,
      status: 'ABORTED',

    }).subscribe({
      next: value => {
        this.animalApiClient.getPregnancyStatus(this.id()).subscribe({
          next: pregnancyHistory => {
            this.pregnancyHistory.set(pregnancyHistory)
            this.calculatePregnancy(pregnancyHistory);
          }
        })
        console.log("added pregnancy status!")
      }
    })
  }

  deletePregnancy(id?: string) {
    this.animalApiClient.deletePregnancy(this.id(), id!).subscribe({
      next: () => {
        this.animalApiClient.getPregnancyStatus(this.id()).subscribe({
          next: pregnancyHistory => {
            this.pregnancyHistory.set(pregnancyHistory)
            this.calculatePregnancy(pregnancyHistory);
          }
        })
      }
    })
  }

  async setYob(year: number) {
    if (this.canBeAltered) {
      this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          yearOfBirth: year.toString()
        }
      });
      await this.dataSynchronisationService.applyAction(
        DataType.animals,
        ActionType.mergeAsset,
        this.currentAnimal()!,
        SynchronizationStatus.syncPending
      );
      this.internalNotificationsStore.addNotification("Successfully changed year of birth.", InternalNotificationType.SUCCESS, false);
    }
  }

  async setAnimalType(animalType: AnimalType) {
    if (this.canBeAltered) {
      this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          type: animalType
        }
      });
      await this.dataSynchronisationService.applyAction(
        DataType.animals,
        ActionType.mergeAsset,
        this.currentAnimal()!,
        SynchronizationStatus.syncPending
      );
      this.internalNotificationsStore.addNotification("Successfully changed animal type.", InternalNotificationType.SUCCESS, false);
    }
  }

  setName($event: any) {
    if (this.canBeAltered) {
      this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          visualTag: $event.target.value
        }
      });
    }
  }

  dateTime(date: string) {
    return new Date(date).toDateString() + " - " + new Date(date).toLocaleTimeString();
  }

  // TODO demo only: fabricates an arrival instant a random few days after the departure instant, cached per gpsFix so it isn't re-randomized on every change detection run
  demoArrivalInstant(gpsFix: Samples.Sample<Samples.NfcGpsFix>) {
    const cached = this.demoArrivalInstants.get(gpsFix);
    if (cached) return cached;

    const randomMillisApart = (6 * 24 + Math.floor(Math.random() * 4 * 24)) * 60 * 60 * 1000; // 6-10 days, randomized time of day
    const arrivalInstant = new Date(new Date(gpsFix.instant).getTime() + randomMillisApart).toISOString();
    this.demoArrivalInstants.set(gpsFix, arrivalInstant);
    return arrivalInstant;
  }

  nameHasChanged() {
    return this.currentAnimal()?.visualTag != this.originalName
  }

  protected metadataHasChanged() {
    return this.currentAnimal()?.animalMetadata != this.originalMetadata
  }

  async setPaddock(paddock: Paddock) {
    if (!this.canBeAltered) return;

    this.assignedPaddock = paddock;

    const paddockId = paddock === unassignedAnimalsPaddock ? undefined : paddock.id;
    this.currentAnimal.update(animal => {
      if (!animal) return undefined;

      return {
        ...animal,
        assignedPaddockId: paddockId
      }
    });
    await this.dataSynchronisationService.applyAction(
      DataType.animals,
      ActionType.mergeAsset,
      this.currentAnimal()!,
      SynchronizationStatus.syncPending
    );
    this.internalNotificationsStore.addNotification("Successfully updated paddock.", InternalNotificationType.SUCCESS, false);
  }

  setComment($event: any) {
    if (this.canBeAltered) {
      this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          comment: $event.target.value
        }
      });
    }
  }

  async applyCommentsChange() {
    if (this.commentHasChanged() && this.canBeAltered) {
      await this.dataSynchronisationService.applyAction(
        DataType.animals,
        ActionType.mergeAsset,
        this.currentAnimal()!,
        SynchronizationStatus.syncPending
      );
      this.originalComment = this.currentAnimal()!.comment;
      this.internalNotificationsStore.addNotification("Successfully changed comment.", InternalNotificationType.SUCCESS, false);
    }
  }

  setProfilePicture() {
    this.router.navigate(['main/animal-details', this.id(), 'set-profile-picture'])
  }

  commentHasChanged() {
    return this.currentAnimal()?.comment != this.originalComment
  }

  protected delete() {
    const animal = this.currentAnimal();
    if (!animal || !this.canBeDeleted) return;

    this.animalSelectionStore.setAllEntities([animal]);
    this.router.navigate(['/main/delete-animals']);
  }

  protected setWeightMeasurement(measurement?: AddWeightMeasurementRequest) {
    this.weightMeasurement = measurement;
  }

  protected async addWeightMeasurement() {
    const assetId = this.id();
    if (!this.weightMeasurement || !assetId) return;

    const measurement: WeightMeasurement = {
      id: randomUuid(),
      animalId: assetId,
      weight: this.weightMeasurement.weight,
      unit: this.weightMeasurement.unit,
      submittedAt: this.weightMeasurement.submittedAt
    }
    this.weightMeasurements.update(measurements => [measurement, ...measurements].sort(this.sortByDescendingSumbittedAt));

    const name = this.currentAnimal()?.visualTag ?? assetId;
    const updates: ScheduledUpdates = {
      id: randomUuid(),
      updates: [
        {
          type: "addWeightMeasurement",
          data: {
            animalId: assetId,
            weightMeasurement: this.weightMeasurement,
          }
        }
      ],
      successMessage: `Successfully added weight measurement for '${name}'.`,
      scheduledMessage: `Weight measurement for '${name}' will be added when you're online again.`,
      failureMessage: `Unable to add weight measurement for '${name}'.`
    }
    await this.dataSynchronisationService.scheduleUpdates(updates);
  }

  private loadBatteryData(assetId: string) {
    this.assetSampleApiClient.getLatestAssetSamples<number>('BatteryVoltage', [assetId]).subscribe({
      next: batteryLevelSamples => {
        const lastBatteryLevelSample = batteryLevelSamples[0];
        if (lastBatteryLevelSample) {
          const lastSeenBatteryLevelDate = new Date(lastBatteryLevelSample.sample?.instant!);
          this.lastSeenBatteryLevelMessage = timeAgo(lastSeenBatteryLevelDate);
        } else {
          this.lastSeenBatteryLevelMessage = 'no detailed battery data available'
        }
      }
    })
  }

  private loadWeightMeasurements() {
    const assetId = this.id();
    if (!assetId) return;

    this.animalApiClient.getWeightMeasurements(assetId).subscribe({
      next: weightMeasurements => {
        weightMeasurements.sort(this.sortByDescendingSumbittedAt);
        this.loadingWeightMeasurements.set(false);
        this.weightMeasurements.set(weightMeasurements);
      }
    })
  }

  private sortByDescendingSumbittedAt(a: WeightMeasurement, b: WeightMeasurement) {
    const timeA = a.submittedAt.getTime();
    const timeB = b.submittedAt.getTime();
    return compare(timeB, timeA);
  }

  private loadAccelerationData(assetId: string) {
    this.assetSampleApiClient.getAssetSamplesSince<Samples.AccelerationCount>('AccelerationCount', [assetId], sinceOneDay()).subscribe({
      next: data => {
        if (data.length === 0) return;

        data.sort(ascendingAssetSamples());

        let numberOfInterrupts = 0;
        for (let i = 0; i < data.length - 1; ++i) {
          const current = data[i].sample?.value?.numberOfInterrupts || 0;
          const previous = data[i + 1].sample?.value?.numberOfInterrupts || 0;
          if (current > previous) numberOfInterrupts += (current - previous);
        }
        this.numberOfInterruptsLast24Hours = numberOfInterrupts;
      }
    });
  }

  async onCopyCoordinates() {
    const coordinates = this.coordinates();
    if (!coordinates || !navigator.clipboard) return;

    const coordString = `${coordinates[0]}, ${coordinates[1]}`
    await navigator.clipboard.writeText(coordString);

    this.internalNotificationsStore.addNotification(`Copied coordinates to clipboard.`, InternalNotificationType.SUCCESS, false);
  }

  loadRssis() {
    const deviceId = this.deviceId();
    if (!deviceId) return;

    this.loadingRssis.set(true);
    const to = new Date();
    const from = new Date(to.getTime() - sevenDays);
    this.rssiService.batteryVoltages([deviceId], from, to).subscribe({
      next: batteryVoltages => {
        this.loadingRssis.set(false);
        this.loadedRssis.set(true);
        if (batteryVoltages.length === 0) {
          this.internalNotificationsStore.addNotification("There's no signal-strength data available for the past week.", InternalNotificationType.INFO, false);
        } else {
          this.rssiSeries.set(calculateSeries(batteryVoltages));
        }
      },
      error: () => {
        this.internalNotificationsStore.addNotification("An unexpected error occured while retrieving signal-strength data.", InternalNotificationType.ERROR, false);
        this.loadingRssis.set(false);
      }
    });
  }

  protected readonly daysUntil = daysUntil;
  protected readonly date = date;

  protected openRelationships() {
    this.router.navigate(['/main/animal-relationships', this.id()])
  }

  protected removeRelationship(parent: string, child: string) {
    this.animalApiClient.removeRelationship(parent, child).subscribe({
      next: () => {
        const state = this.aggregatedAssetStateStore.entityMap()[this.id()];
        if (state) {
          this.aggregatedAssetStateStore.setEntity({
            ...state,
            parents: (state.parents || []).filter(x => x.id !== parent),
            children: (state.children || []).filter(x => x.id !== child)
          });
        }
      }
    })
  }

  protected async setMetadata($event: any, type: string) {
    switch (type) {
      case 'rfid': this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          animalMetadata: {...animal.animalMetadata, rfid: $event.target.value}
        }
      });
        break;
      case 'sisbov': this.currentAnimal.update(animal => {
        if (!animal) return undefined;

        return {
          ...animal,
          animalMetadata: {...animal.animalMetadata, sisbov: $event.target.value}
        }
      });
        break;
    }
  }

  async applyChanges() {
    await this.dataSynchronisationService.applyAction(
      DataType.animals,
      ActionType.mergeAsset,
      this.currentAnimal()!,
      SynchronizationStatus.syncPending
    );
  }

  protected readonly timeAgo = timeAgo;

  protected nfcButtonText() {
    return `Total NFC Location: ${this.nfcGpsFixes().length } Last scanned: ${timeAgo(this.nfcScanMoments().at(this.nfcScanMoments().length - 1)?.scannedAt, undefined,'never')}`;
  }
}

function daysUntil(d?: Date | string) {
  if (!d) return -1
  let dueDate: Date
  if (typeof (d) == "string") {
    dueDate = new Date(Date.parse(d))
  } else {
    dueDate = d
  }
  const date = new Date();
  return Math.round((dueDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
}

function date(d?: string) {
  if (!d) return '-'
  return new Date(Date.parse(d)).toDateString()
}

function getDueDate(startingDate?: Date) {
  if (startingDate) {
    return new Date(startingDate.getTime() + 283 * 24 * 60 * 60 * 1000);
  }
  return new Date(new Date().getTime() + 283 * 24 * 60 * 60 * 1000);
}
